import React, { useEffect, useRef, useState } from 'react';

/**
 * Keeps an expanded row the width of what you can actually see.
 *
 * An expanded row lives in a `<td colSpan>` of the list table. If its contents
 * are wider than the viewport — and a full line-items table is — that cell
 * stretches, which stretches the list table, which makes the OUTER container
 * scroll. The result is that dragging sideways to reach a column also drags
 * the requisition header, the review summary and everything else off-screen.
 *
 * Pinning the panel to the scroll container's visible width stops the outer
 * table growing, so the inner table's own `overflow-x` is what moves — the
 * line items scroll, and nothing around them does.
 *
 * Measured rather than assumed: the container's width depends on the sidebar,
 * the window and the zoom level, none of which are known ahead of time.
 */

export function useScrollViewport(deps = []) {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setWidth(el.clientWidth);
    measure();
    // Re-measures on window resize, sidebar collapse and zoom — a fixed value
    // read once would be wrong the moment any of those changed.
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { ref, width };
}

/** The sticky, width-capped wrapper an expanded row's content sits in. */
export function PinnedDetail({ width, children }) {
  return (
    <div style={{
      // sticky-left holds the panel still while the list scrolls behind it.
      position: 'sticky',
      left: 0,
      width: width ? `${width}px` : '100%',
      maxWidth: width ? `${width}px` : '100vw',
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr)',
    }}>
      <div style={{ minWidth: 0, overflow: 'hidden' }}>
        {children}
      </div>
    </div>
  );
}
